/**
 * 全屏页（popupLayoutMode === 'fullscreen'）下的配置归一化：
 * - 关闭「适配推荐」、禁止点击外部关闭
 * - 存量标准弹窗切换全屏时，若为上图/中图，自动转为左图（popWindowWheel=2 保留原值）
 * - 图片位置限制在左/右/无/背景
 * - 全屏 + 背景图时不使用背景蒙版（清空蒙版开关）
 * - 容器圆角固定为 0px 并写入 styleMap
 *
 * 纯转盘模板（popWindowWheel === 1）全屏时仅支持左图/右图
 * 侧图+转盘模板（popWindowWheel === 2）全屏时仅支持左图/右图（保持现状）
 * 其他模板全屏时支持左图/右图/无图/背景图
 * popWindowWheel=3 全屏增强模板仅支持无图
 */
const FULLSCREEN_IMG_POSITIONS = ['left', 'right', 'none', 'background'];
const WHEEL3_IMG_POSITIONS = ['none'];
const PURE_WHEEL_FULLSCREEN_POSITIONS = ['left', 'right'];
const WHEEL_COMBO2_FULLSCREEN_POSITIONS = ['left', 'right'];
const FULLSCREEN_CONTAINER_BORDER_RADIUS = '0px';
const FULLSCREEN_CONTAINER_STYLE_KEYS = [
  'cartx-b-wheel-container',
  'cartx-b-wheel-container__middle',
  'cartx-b-wheel-container__end',
];

/** 全屏模式下容器圆角固定为 0，并写入 styleMap 供 C 端读取 */
export function normalizeFullscreenContainerBorderRadius(
  settings: any,
  options?: { capturePrev?: boolean }
) {
  const styleMap = settings?.styles?.styleMap;
  if (!styleMap) {
    return;
  }
  const rules = settings?.rules;
  if (options?.capturePrev && rules) {
    const prev: Record<string, string> = {};
    FULLSCREEN_CONTAINER_STYLE_KEYS.forEach((key) => {
      const value = styleMap[key]?.['border-radius'];
      if (value) {
        prev[key] = value;
      }
    });
    if (Object.keys(prev).length > 0) {
      rules._prevFullscreenContainerBorderRadius = prev;
    }
  }
  FULLSCREEN_CONTAINER_STYLE_KEYS.forEach((key) => {
    if (key !== 'cartx-b-wheel-container' && !styleMap[key]) {
      return;
    }
    if (!styleMap[key]) {
      styleMap[key] = {};
    }
    styleMap[key]['border-radius'] = FULLSCREEN_CONTAINER_BORDER_RADIUS;
  });
}

/** 从全屏切回标准弹窗时恢复切换前记录的容器圆角 */
export function restoreFullscreenContainerBorderRadius(settings: any) {
  const styleMap = settings?.styles?.styleMap;
  const rules = settings?.rules;
  const prev = rules?._prevFullscreenContainerBorderRadius;
  if (!styleMap || !prev || typeof prev !== 'object') {
    return;
  }
  Object.entries(prev).forEach(([key, value]) => {
    if (styleMap[key]) {
      styleMap[key]['border-radius'] = value;
    }
  });
  delete rules._prevFullscreenContainerBorderRadius;
}

type NormalizePopupLayoutRulesOptions = {
  /** 为 true 时记录切换前的 top/middle 位置（仅在「标准 → 全屏」切换时传入） */
  capturePrevOnEnter?: boolean;
};

export function normalizePopWindowWheel3ImgPosition(data: any) {
  const settings = data?.settings || data;
  if (Number(settings?.popWindowWheel) !== 3 || !settings?.typesetting) {
    return;
  }
  const ts = settings.typesetting;
  ts.imgPosition = 'none';
  ts.middleImgPosition = 'none';
  ts.endImgPosition = 'none';
}

export function normalizePopupLayoutRules(data: any, options?: NormalizePopupLayoutRulesOptions) {
  // 兼容两种入参：
  // 1) settings 对象本身（含 rules/typesetting）
  // 2) 外层 formData（含 settings.rules/settings.typesetting）
  const settings = data?.settings || data;
  if (!settings?.rules) {
    return;
  }

  normalizePopWindowWheel3ImgPosition(data);

  if (settings.rules.popupLayoutMode !== 'fullscreen') {
    return;
  }
  const rules = settings.rules;
  normalizeFullscreenContainerBorderRadius(settings, {
    capturePrev: options?.capturePrevOnEnter,
  });
  rules.adapterText = false;
  rules.middleAdapterText = false;
  rules.clickBackgroundCloseOption = false;

  // 判断是否为纯转盘模板（popWindowWheel === 1）
  const popWindowWheel = Number(settings?.popWindowWheel);
  const isPopWindowWheel3 = popWindowWheel === 3;
  const isPureWheelTemplate = popWindowWheel === 1;
  const isWheelCombo2 = popWindowWheel === 2;
  const allowedPositions = isPopWindowWheel3
    ? WHEEL3_IMG_POSITIONS
    : isPureWheelTemplate
      ? PURE_WHEEL_FULLSCREEN_POSITIONS
      : isWheelCombo2
        ? WHEEL_COMBO2_FULLSCREEN_POSITIONS
        : FULLSCREEN_IMG_POSITIONS;
  const defaultPosition = isPopWindowWheel3
    ? 'none'
    : isPureWheelTemplate
      ? 'left'
      : isWheelCombo2
        ? 'right'
        : 'none';

  const ts = settings.typesetting || {};
  if (options?.capturePrevOnEnter) {
    const prev: Record<string, string> = {};
    if (!isWheelCombo2) {
      ['imgPosition', 'middleImgPosition', 'endImgPosition'].forEach((k) => {
        const v = ts[k];
        if (v === 'top' || v === 'middle') {
          prev[k] = v;
        }
      });
    } else {
      if (ts.imgPosition === 'middle') prev.imgPosition = 'middle';
      if (ts.wheelPosition === 'middle') prev.wheelPosition = 'middle';
    }
    if (Object.keys(prev).length > 0) {
      rules._prevFullscreenImgPositions = prev;
    }
  }

  const normalizeLegacyTopMiddleToLeft = (key: string) => {
    const value = ts[key];
    if (value === 'top' || value === 'middle') {
      ts[key] = 'left';
    }
  };
  // combo2 全屏保持已有图片位置，不在切换时强制改成 left
  if (!isWheelCombo2) {
    normalizeLegacyTopMiddleToLeft('imgPosition');
    normalizeLegacyTopMiddleToLeft('middleImgPosition');
    normalizeLegacyTopMiddleToLeft('endImgPosition');
  }

  const fixPosition = (key: string) => {
    const v = ts[key];
    // 如果值不在允许的全屏位置列表中，设置为默认值
    if (!v || !allowedPositions.includes(v)) {
      ts[key] = defaultPosition;
    }
  };
  // combo2 全屏仅用于展示约束，不改写存量位置值，避免回切标准弹窗后样式被联动影响
  if (!isWheelCombo2) {
    fixPosition('imgPosition');
    fixPosition('middleImgPosition');
    fixPosition('endImgPosition');
  }

  // 纯转盘模板不支持背景图模式，跳过蒙版相关处理
  if (!isPureWheelTemplate) {
    if (ts.imgPosition === 'background') {
      ts.bgbox = false;
    }
    if (ts.middleImgPosition === 'background') {
      ts.middleBgbox = false;
    }
    if (ts.endImgPosition === 'background') {
      ts.endBgbox = false;
    }
  }
}

/**
 * 是否展示「中图 → 全屏」相关提示：
 * - 当前为全屏页；
 * - 且由标准弹窗切换而来（normalize 写入 _prevFullscreenImgPositions）；
 * - 且切换前至少有一处位置为 middle（中图）。
 */
export function shouldShowFullscreenMiddlePositionTip(data: any): boolean {
  const settings = data?.settings || data;
  const rules = settings?.rules;
  if (!rules || rules.popupLayoutMode !== 'fullscreen') {
    return false;
  }
  const prev = rules._prevFullscreenImgPositions;
  if (!prev || typeof prev !== 'object') {
    return false;
  }
  return Object.values(prev).some((v) => v === 'middle');
}

/** 各页图片/转盘位置：仅当该页切换全屏前为 middle 时展示提示 */
export function shouldShowFullscreenMiddlePositionTipForPage(
  data: any,
  pageKey: 'imgPosition' | 'middleImgPosition' | 'endImgPosition' | 'wheelPosition'
): boolean {
  const settings = data?.settings || data;
  const rules = settings?.rules;
  if (!rules || rules.popupLayoutMode !== 'fullscreen') {
    return false;
  }
  const prev = rules._prevFullscreenImgPositions;
  if (!prev || typeof prev !== 'object') {
    return false;
  }
  return prev[pageKey] === 'middle';
}
